// src/utils/passkeyUtils.js
import awsExports from "../aws-exports";
import { getCognitoDomain } from "./authUtils";
// LocalStorage key for passkey reminder preference
export const PASSKEY_REMINDER_KEY = "skipPasskeyReminder";

// Cognito configuration
const COGNITO_CONFIG = {
  clientId: awsExports.aws_user_pools_web_client_id,
  cognitoDomain: getCognitoDomain(),
  region: awsExports.aws_cognito_region,
};

/**
 * Build the passkey setup URL for Cognito
 * @returns {string} The full passkey setup URL
 */
export const getPasskeySetupUrl = () => {
  const { clientId, cognitoDomain } = COGNITO_CONFIG;
  const redirectUri = window.location.origin;
  const scopes = "email openid phone aws.cognito.signin.user.admin";

  return `${cognitoDomain}/passkeys/add?client_id=${clientId}&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}&response_type=code&scope=${encodeURIComponent(scopes)}`;
};

/**
 * Check if user has opted out of passkey reminders
 * @returns {boolean} True if user doesn't want reminders
 */
export const shouldSkipPasskeyReminder = () => {
  return localStorage.getItem(PASSKEY_REMINDER_KEY) === "true";
};

/**
 * Set user preference to skip passkey reminders
 * @param {boolean} skip - Whether to skip reminders
 */
export const setSkipPasskeyReminder = (skip) => {
  if (skip) {
    localStorage.setItem(PASSKEY_REMINDER_KEY, "true");
  } else {
    localStorage.removeItem(PASSKEY_REMINDER_KEY);
  }
};

/**
 * Check if user has a passkey configured in Cognito
 * @param {string} accessToken - The user's Cognito access token
 * @param {string} region - AWS region (optional, defaults to us-east-1)
 * @returns {Promise<{hasPasskey: boolean, error: boolean}>}
 */
export const checkPasskeyStatus = async (
  accessToken,
  region = COGNITO_CONFIG.region,
) => {
  try {
    const response = await fetch(
      `https://cognito-idp.${region}.amazonaws.com/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target":
            "AWSCognitoIdentityProviderService.ListWebAuthnCredentials",
        },
        body: JSON.stringify({
          AccessToken: accessToken,
        }),
      },
    );

    if (response.ok) {
      const data = await response.json();
      const hasPasskey = data.Credentials && data.Credentials.length > 0;

      // Clear skip reminder if user now has a passkey
      if (hasPasskey) {
        setSkipPasskeyReminder(false);
      }

      return { hasPasskey, error: false };
    } else {
      console.warn("Could not check passkey status", response.status);
      return { hasPasskey: false, error: true };
    }
  } catch (e) {
    console.error("Error checking passkey status", e);
    return { hasPasskey: false, error: true };
  }
};

/**
 * Determine if passkey dialog should be shown
 * @param {boolean} hasPasskey - Whether user has passkey
 * @param {boolean} error - Whether there was an error checking
 * @returns {boolean} True if dialog should be shown
 */
export const shouldShowPasskeyDialog = (hasPasskey, error) => {
  // Don't show if user opted out or has error or already has passkey
  if (shouldSkipPasskeyReminder() || error || hasPasskey) {
    return false;
  }
  return true;
};

/**
 * Redirect user to passkey setup page
 */
export const redirectToPasskeySetup = () => {
  window.location.href = getPasskeySetupUrl();
};
